import { memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { UiModal } from 'src/components/UI/modal/UiModal';
import { BaseSeparateModal } from 'src/models/modals';
import { useTHelper } from 'src/utils/i18n';
import { UiCleanInput } from 'src/components/UI/inputs/CleanInput';
import { useStateForm } from 'src/utils/stateForm';
import { currentUserSelector } from 'src/store/user/selectors';
import { dispatchAnyRequestStart } from 'src/store/anyRequest/helpers';
import { apiSendContactOrganizerEmail } from 'src/api/contactOrganizer';
import { useSetHoverErrors } from 'src/utils/hooks/useSetHoverErrors';
import { UiToast } from 'src/components/UI/toasts';
import { toggleGlobalLoader } from 'src/store/commonPart/slice';
import { UiMereLabel } from 'src/components/UI/UiMereLabel';

type Props = BaseSeparateModal & {
  permanenturl: string;
};

type FormValues = {
  Permanenturl: string;
  Subject: string;
  Message: string;
  Name: string;
  Email: string | null;
};

export const CheckoutConfirmationContactOrganizerModal = memo<Props>(({ onClose, permanenturl }) => {
  const dispatch = useDispatch();

  const tHelper = useTHelper('checkout.confirmation.contactOrganizer.modal');

  const { response } = useSelector(currentUserSelector);

  const formProps = useStateForm<FormValues>({
    defaultValues: {
      Permanenturl: permanenturl,
    },
  });

  useEffect(() => {
    if (response) {
      formProps.reset({
        Name: response.firstName?.length ? `${response.firstName} ${response.lastName}` : '',
        Email: response.email,
      });
    }
  }, [formProps, response]);

  const uniqueId = 'sendEmailToOrganizer';

  const {
    response: { loading },
    errorTextList,
    clearErrors,
  } = useSetHoverErrors(uniqueId, formProps.setError, {
    onSuccess: () => {
      dispatch(toggleGlobalLoader(false));
      UiToast.success(tHelper('messageSent'));
      onClose();
    },
    onError: () => {
      dispatch(toggleGlobalLoader(false));
    },
  });

  const sendEmailToOrganizer = useCallback(() => {
    formProps.onSubmit((data) => {
      dispatch(toggleGlobalLoader(true));

      dispatchAnyRequestStart({
        data,
        apiFunction: apiSendContactOrganizerEmail,
        uniqueId,
      });
    })();
  }, [dispatch, formProps]);

  return (
    <UiModal
      id="ContactOrganizerDialog"
      onHide={onClose}
      header={tHelper('header')}
      successBtnText={tHelper('send')}
      successBtnClassProperty="primaryNew"
      successLoading={loading}
      onSuccess={sendEmailToOrganizer}
      validationErrors={errorTextList}
      onCloseValidationErrors={clearErrors}
    >
      <UiMereLabel>{tHelper('yourMessage')}</UiMereLabel>
      <UiCleanInput
        name="Subject"
        formProps={formProps}
        placeholder={tHelper('placeholder.subject')}
        maxLength={75}
        material
        defaultPlaceholderStyle
        required
      />
      <UiCleanInput
        type="textarea"
        name="Message"
        formProps={formProps}
        placeholder={tHelper('placeholder.message')}
        maxLength={5000}
        rows={5}
        material
        defaultPlaceholderStyle
        required
      />

      <UiMereLabel>{tHelper('letKnow')}</UiMereLabel>
      <UiCleanInput
        name="Name"
        formProps={formProps}
        placeholder={tHelper('placeholder.name')}
        maxLength={60}
        material
        defaultPlaceholderStyle
        required
      />
      <UiCleanInput
        type="email"
        name="Email"
        formProps={formProps}
        placeholder={tHelper('placeholder.email')}
        maxLength={60}
        material
        defaultPlaceholderStyle
        required
      />
    </UiModal>
  );
});
